import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  FaPlus, FaSearch, FaEdit, FaTrash, FaEye, FaDownload,
  FaTimes, FaSave, FaUser, FaUsers, FaEnvelope, FaPhone,
  FaGlobe, FaBuilding, FaMapMarkerAlt, FaSort, FaSortUp,
  FaSortDown, FaFilter, FaChevronLeft, FaChevronRight,
  FaToggleOn, FaToggleOff, FaAngleDown, FaStar, FaRegStar,
  FaExclamationTriangle, FaCheckCircle, FaBan, FaTag,
  FaFileExport, FaUserPlus, FaCopy
} from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

/* ─── Inline Styles (Holiday-matching design) ──────────── */
const S = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .cm-wrap {
    font-family: 'DM Sans', sans-serif;
    background: #FFFFFF;
    min-height: 100vh;
    color: #1a1a1a;
    padding-top: 120px;
  }

  .cm-page {
    padding: 0 24px 40px;
    width: 100%;
    max-width: 100%;
    margin: 0;
  }

  /* ── Header ── */
  .cm-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 28px;
  }
  .cm-title {
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.5px;
    color: #111;
    margin: 0;
  }
  .cm-subtitle {
    font-size: 13px;
    color: #888;
    margin: 4px 0 0;
    font-weight: 400;
  }
  .cm-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  /* ── Buttons ── */
  .btn-ghost {
    background: #fff;
    border: 1px solid #e2e2e2;
    color: #444;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-ghost:hover { background: #f0f0ee; border-color: #ccc; }

  .btn-primary-cm {
    background: #0052CC;
    border: 1px solid #0052CC;
    color: #fff;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-primary-cm:hover { background: #003d99; }
  .btn-primary-cm:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-danger-cm {
    background: #c0392b;
    border: 1px solid #c0392b;
    color: #fff;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-danger-cm:hover { background: #a93226; }

  .btn-warning-cm {
    background: #e67e22;
    border: 1px solid #e67e22;
    color: #fff;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-warning-cm:hover { background: #cf6d17; }

  /* ── Stats Row ── */
  .cm-stats-row {
    background: #fff;
    border: 1px solid #e8e8e6;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }
  .cm-stats-label {
    font-size: 13px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    white-space: nowrap;
  }
  .cm-stat-pills {
    display: flex;
    gap: 10px;
  }
  .cm-stat-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-radius: 20px;
    border: 1px solid #e2e2e2;
    background: #fafaf9;
    font-size: 13px;
    font-weight: 500;
    color: #555;
  }
  .cm-stat-pill.active-pill {
    background: #edf5ed;
    border-color: #c8e6c9;
    color: #356035;
  }
  .cm-stat-pill.inactive-pill {
    background: #f5f5f3;
    border-color: #e2e2e2;
    color: #888;
  }
  .cm-stat-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  /* ── Toolbar ── */
  .cm-toolbar {
    background: #fff;
    border: 1px solid #e8e8e6;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 16px;
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }
  .cm-search {
    flex: 1;
    min-width: 200px;
    position: relative;
  }
  .cm-search input {
    width: 100%;
    padding: 8px 12px 8px 36px;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    background: #fafaf9;
    color: #222;
    outline: none;
    transition: border 0.15s;
    box-sizing: border-box;
  }
  .cm-search input:focus { border-color: #999; background: #fff; }
  .cm-search-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 14px;
    pointer-events: none;
  }
  .cm-select {
    padding: 8px 12px;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    background: #fafaf9;
    color: #333;
    outline: none;
    cursor: pointer;
    transition: border 0.15s;
  }
  .cm-select:focus { border-color: #999; }

  /* ── Table Card ── */
  .cm-table-card {
    background: #fff;
    border: 1px solid #e8e8e6;
    border-radius: 12px;
    overflow: hidden;
  }
  .cm-table-wrap {
    overflow-x: auto;
    max-height: 58vh;
    overflow-y: auto;
  }
  table.cm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  table.cm-table thead th {
    background: #fafaf9;
    border-bottom: 1px solid #e8e8e6;
    padding: 11px 16px;
    text-align: left;
    font-weight: 600;
    color: #666;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s;
  }
  table.cm-table thead th:hover { background: #f0f0ee; }
  .th-inner {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  table.cm-table tbody tr {
    border-bottom: 1px solid #f0f0ee;
    transition: background 0.1s;
  }
  table.cm-table tbody tr:last-child { border-bottom: none; }
  table.cm-table tbody tr:hover { background: #fafaf9; }
  table.cm-table tbody td {
    padding: 11px 16px;
    color: #333;
    vertical-align: middle;
  }

  /* ── Cell Styles ── */
  .cm-code-badge {
    font-size: 12px;
    font-weight: 500;
    color: #3a6090;
    background: #eaf0f8;
    border-radius: 4px;
    padding: 2px 8px;
    font-family: 'DM Mono', monospace;
  }
  .cm-name-cell {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .cm-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    flex-shrink: 0;
    background: #eaf0f8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: #3a6090;
  }
  .cm-name-text {
    font-weight: 500;
    color: #111;
  }
  .cm-sub-text {
    font-size: 11.5px;
    color: #999;
    margin-top: 1px;
  }

  /* Status badge */
  .cm-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    gap: 5px;
  }
  .cm-status-badge.active { background: #edf5ed; color: #356035; }
  .cm-status-badge.inactive { background: #f5f5f3; color: #888; }
  .cm-status-badge.blacklisted { background: #fdf0ee; color: #c0392b; }
  .cm-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  /* Type chip */
  .cm-type-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    background: #f5f0e8;
    color: #7a5a2a;
  }
  .cm-type-chip.individual { background: #eaf0f8; color: #3a6090; }
  .cm-type-chip.company { background: #f5f0e8; color: #7a5a2a; }
  .cm-type-chip.government { background: #edf5ed; color: #356035; }
  .cm-type-chip.ngo { background: #f5edf5; color: #603560; }

  /* Row Actions */
  .row-action {
    background: none;
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s;
  }
  .row-contacts { color: #356035; }
  .row-contacts:hover { background: #edf5ed; }
  .row-view { color: #555; }
  .row-view:hover { background: #f0f0ee; }
  .row-edit { color: #3a6090; }
  .row-edit:hover { background: #eaf0f8; }
  .row-deactivate { color: #e67e22; }
  .row-deactivate:hover { background: #fef5e7; }
  .row-del { color: #c0392b; }
  .row-del:hover { background: #fdf0ee; }

  /* ── Empty ── */
  .cm-empty {
    padding: 56px;
    text-align: center;
    color: #aaa;
    font-size: 14px;
  }

  /* ── Footer / Pagination ── */
  .cm-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    border-top: 1px solid #f0f0ee;
    font-size: 12.5px;
    color: #888;
  }
  .cm-pagination { display: flex; gap: 4px; }
  .pg-btn {
    background: #fff;
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 12px;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    color: #444;
    transition: all 0.12s;
  }
  .pg-btn:hover:not(:disabled) { background: #f5f5f3; border-color: #bbb; }
  .pg-btn.active { background: #0052CC; color: #fff; border-color: #0052CC; }
  .pg-btn:disabled { opacity: 0.4; cursor: default; }

  /* ── Loading ── */
  .cm-loading {
    padding: 64px;
    text-align: center;
    color: #aaa;
    font-size: 14px;
  }
  .cm-spinner {
    display: inline-block;
    width: 20px; height: 20px;
    border: 2px solid #e2e2e2;
    border-top-color: #555;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    margin-right: 10px;
    vertical-align: middle;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Modal ── */
  .cm-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.35);
    backdrop-filter: blur(3px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .cm-modal {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 700px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.15);
    overflow: hidden;
    animation: modalIn 0.2s ease;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }
  .cm-modal.sm { max-width: 480px; }
  .cm-modal.lg { max-width: 860px; }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to   { opacity: 1; transform: none; }
  }

  .cm-modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f0f0ee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }
  .cm-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: #111;
    margin: 0;
  }
  .cm-modal-sub {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }
  .cm-modal-close {
    background: #f5f5f3;
    border: none;
    width: 28px; height: 28px;
    border-radius: 50%;
    font-size: 16px;
    cursor: pointer;
    color: #666;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s;
  }
  .cm-modal-close:hover { background: #eaeae8; }

  .cm-modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 22px 24px;
  }
  .cm-modal-body::-webkit-scrollbar { width: 5px; }
  .cm-modal-body::-webkit-scrollbar-thumb { background: #ddd; border-radius: 10px; }

  .cm-modal-footer {
    padding: 14px 24px;
    border-top: 1px solid #f0f0ee;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
  }

  /* ── Form Elements ── */
  .cm-section-title {
    font-size: 11.5px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0ee;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .cm-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 22px;
  }
  .cm-form-grid.cols-3 { grid-template-columns: 1fr 1fr 1fr; }
  .cm-field { display: flex; flex-direction: column; gap: 5px; }
  .cm-field.span2 { grid-column: span 2; }
  .cm-field.span3 { grid-column: span 3; }
  .cm-label {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .cm-label .req { color: #c0392b; margin-left: 3px; }
  .cm-input, .cm-textarea, .cm-fieldselect {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    color: #222;
    background: #fafaf9;
    outline: none;
    transition: border 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }
  .cm-input::placeholder, .cm-textarea::placeholder { color: #bbb; }
  .cm-input:focus, .cm-textarea:focus, .cm-fieldselect:focus {
    border-color: #888;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
  }
  .cm-input.readonly {
    background: #f0f0ee;
    color: #888;
    font-weight: 500;
    cursor: default;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    letter-spacing: 0.3px;
  }
  .cm-textarea { resize: vertical; min-height: 78px; }
  .cm-fieldselect {
    appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23aaa' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 30px;
  }
  .cm-field-hint {
    font-size: 11px;
    color: #aaa;
    margin-top: 2px;
  }
  .cm-input-group { position: relative; }
  .cm-input-prefix {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 12px;
    pointer-events: none;
  }
  .cm-input.with-prefix { padding-left: 30px; }

  /* ── Tags Input ── */
  .cm-tags-input-wrap {
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    background: #fafaf9;
    padding: 7px 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    transition: border 0.15s, box-shadow 0.15s;
    min-height: 40px;
    cursor: text;
  }
  .cm-tags-input-wrap:focus-within {
    border-color: #888;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
  }
  .cm-tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eaf0f8;
    color: #3a6090;
    border: 1px solid #d0dff0;
    border-radius: 20px;
    padding: 2px 9px;
    font-size: 12px;
    font-weight: 500;
  }
  .cm-tag-rm {
    background: none;
    border: none;
    cursor: pointer;
    color: #3a6090;
    padding: 0 1px;
    display: flex;
    align-items: center;
    font-size: 10px;
    opacity: 0.6;
    transition: opacity 0.15s;
  }
  .cm-tag-rm:hover { opacity: 1; }
  .cm-tags-text-input {
    border: none;
    outline: none;
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: #222;
    flex: 1;
    min-width: 80px;
  }
  .cm-tags-text-input::placeholder { color: #bbb; }

  /* ── Contact Cards ── */
  .cm-contacts-list { display: flex; flex-direction: column; gap: 10px; }
  .cm-contact-card {
    background: #fafaf9;
    border: 1px solid #e8e8e6;
    border-radius: 10px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: border 0.15s;
  }
  .cm-contact-card:hover { border-color: #ccc; }
  .cm-contact-card.primary-contact {
    border-color: #0052CC;
    background: #f0f6ff;
  }
  .cm-contact-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    flex-shrink: 0;
    background: #eaf0f8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #3a6090;
  }
  .cm-contact-info { flex: 1; min-width: 0; }
  .cm-contact-name {
    font-size: 13.5px;
    font-weight: 600;
    color: #111;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .cm-primary-badge {
    font-size: 10px;
    font-weight: 600;
    background: #0052CC;
    color: #fff;
    border-radius: 20px;
    padding: 2px 7px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .cm-contact-details {
    font-size: 12px;
    color: #888;
    margin-top: 3px;
  }
  .cm-contact-actions { display: flex; gap: 4px; flex-shrink: 0; }

  /* ── Confirm Modal ── */
  .cm-confirm-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }
  .cm-confirm-icon.danger { background: #fdf0ee; color: #c0392b; }
  .cm-confirm-icon.warning { background: #fef5e7; color: #e67e22; }
  .cm-confirm-title {
    font-size: 17px;
    font-weight: 600;
    color: #111;
    text-align: center;
    margin-bottom: 8px;
  }
  .cm-confirm-msg {
    font-size: 13.5px;
    color: #888;
    text-align: center;
    line-height: 1.6;
  }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .cm-page { padding: 0 16px 40px; }
    .cm-header { flex-direction: column; gap: 14px; }
    .cm-form-grid { grid-template-columns: 1fr; }
    .cm-form-grid.cols-3 { grid-template-columns: 1fr; }
    .cm-field.span2, .cm-field.span3 { grid-column: span 1; }
    .cm-toolbar { gap: 8px; }
  }
`;

/* ─── Constants ──────────────────────────────────────────── */
const INDUSTRIES = [
  "IT", "Construction", "Manufacturing", "Consulting", "Marketing",
  "Healthcare", "Finance", "Education", "Other"
];
const CUSTOMER_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const PAYMENT_TERMS = ["Net 15", "Net 30", "Net 45", "Due on receipt"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];
const PAGE_SIZE = 10;

/* ─── Helpers ────────────────────────────────────────────── */
const initials = (name = "") =>
  name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "??";

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/* ─── Blank Forms ─────────────────────────────────────────── */
const blankCustomer = () => ({
  customerName: "", customerCode: "", customerType: "Company",
  industry: "", website: "", addressLine1: "", addressLine2: "",
  city: "", state: "", country: "", postalCode: "",
  primaryContactName: "", primaryContactEmail: "", primaryContactPhone: "",
  accountManager: "", status: "Active", currency: "INR",
  paymentTerms: "Net 30", taxNumber: "", notes: "", tags: []
});

const blankContact = () => ({
  name: "", email: "", phone: "", designation: "",
  department: "", address: "", notes: "", isPrimary: false
});

/* ─── Sub-components ─────────────────────────────────────── */

function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <FaSort style={{ opacity: 0.25, fontSize: 10 }} />;
  return sortDir === "asc"
    ? <FaSortUp style={{ color: "#0052CC", fontSize: 10 }} />
    : <FaSortDown style={{ color: "#0052CC", fontSize: 10 }} />;
}

function StatusBadge({ status }) {
  const s = status?.toLowerCase();
  return (
    <span className={`cm-status-badge ${s}`}>
      <span className="cm-status-dot" />
      {status}
    </span>
  );
}

function TypeChip({ type }) {
  const t = type?.toLowerCase() || "";
  return <span className={`cm-type-chip ${t}`}>{type || "—"}</span>;
}

function TagInput({ tags = [], onChange }) {
  const [inp, setInp] = useState("");
  const addTag = (val) => {
    const t = val.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInp("");
  };
  return (
    <div className="cm-tags-input-wrap">
      {tags.map(t => (
        <span key={t} className="cm-tag-chip">
          {t}
          <button className="cm-tag-rm" onClick={() => onChange(tags.filter(x => x !== t))}>
            <FaTimes />
          </button>
        </span>
      ))}
      <input
        className="cm-tags-text-input"
        placeholder={tags.length ? "" : "Add tag, press Enter…"}
        value={inp}
        onChange={e => setInp(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(inp); }
          if (e.key === "Backspace" && !inp && tags.length) onChange(tags.slice(0, -1));
        }}
        onBlur={() => inp.trim() && addTag(inp)}
      />
    </div>
  );
}

/* ─── Customer Form Modal ─────────────────────────────────── */
function CustomerModal({ customer, onClose, onSaved, token }) {
  const isEdit = !!customer?.id;
  const [form, setForm] = useState(isEdit ? { ...customer } : blankCustomer());
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.customerName.trim()) { toast.warning("Customer name is required."); return; }
    if (!form.customerType) { toast.warning("Customer type is required."); return; }
    if (!form.status) { toast.warning("Status is required."); return; }
    if (!form.currency) { toast.warning("Currency is required."); return; }
    if (form.primaryContactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.primaryContactEmail)) {
      toast.warning("Invalid primary contact email."); return;
    }
    if (form.website && !/^https?:\/\/.+/.test(form.website)) {
      toast.warning("Website must start with http:// or https://"); return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_BASE_URL}/api/customers/update/${customer.id}`
        : `${API_BASE_URL}/api/customers/add`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      toast.success(isEdit ? "Customer updated!" : "Customer created!");
      onSaved();
    } catch {
      toast.error(isEdit ? "Failed to update customer." : "Failed to create customer.");
    } finally { setSaving(false); }
  };

  return (
    <div className="cm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cm-modal lg">
        <div className="cm-modal-header">
          <div>
            <div className="cm-modal-title">{isEdit ? "Edit Customer" : "Add Customer"}</div>
            <div className="cm-modal-sub">{isEdit ? `Editing ${customer.customerName}` : "Fill in details to add a new customer"}</div>
          </div>
          <button className="cm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="cm-modal-body">
          {/* Basic Info */}
          <div className="cm-section-title"><FaBuilding /> Basic Information</div>
          <div className="cm-form-grid">
            <div className="cm-field">
              <label className="cm-label">Customer Code</label>
              <input className="cm-input readonly" value={form.customerCode || "Auto-generated"} readOnly />
              <span className="cm-field-hint">Format: CUST-001 — auto-assigned on creation</span>
            </div>
            <div className="cm-field">
              <label className="cm-label">Customer Name <span className="req">*</span></label>
              <input className="cm-input" placeholder="e.g. Acme Corporation" value={form.customerName}
                onChange={e => set("customerName", e.target.value)} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Customer Type <span className="req">*</span></label>
              <select className="cm-fieldselect" value={form.customerType}
                onChange={e => set("customerType", e.target.value)}>
                {CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="cm-field">
              <label className="cm-label">Industry</label>
              <select className="cm-fieldselect" value={form.industry}
                onChange={e => set("industry", e.target.value)}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="cm-field span2">
              <label className="cm-label">Website</label>
              <div className="cm-input-group">
                <FaGlobe className="cm-input-prefix" />
                <input className="cm-input with-prefix" placeholder="https://example.com" value={form.website}
                  onChange={e => set("website", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="cm-section-title"><FaMapMarkerAlt /> Address Details</div>
          <div className="cm-form-grid">
            <div className="cm-field span2">
              <label className="cm-label">Address Line 1</label>
              <input className="cm-input" placeholder="Street address" value={form.addressLine1}
                onChange={e => set("addressLine1", e.target.value)} />
            </div>
            <div className="cm-field span2">
              <label className="cm-label">Address Line 2</label>
              <input className="cm-input" placeholder="Apartment, suite, building (optional)" value={form.addressLine2}
                onChange={e => set("addressLine2", e.target.value)} />
            </div>
            <div className="cm-field">
              <label className="cm-label">City</label>
              <input className="cm-input" placeholder="City" value={form.city}
                onChange={e => set("city", e.target.value)} />
            </div>
            <div className="cm-field">
              <label className="cm-label">State / Province</label>
              <input className="cm-input" placeholder="State" value={form.state}
                onChange={e => set("state", e.target.value)} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Country</label>
              <input className="cm-input" placeholder="Country" value={form.country}
                onChange={e => set("country", e.target.value)} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Postal Code</label>
              <input className="cm-input" placeholder="PIN / ZIP" value={form.postalCode}
                onChange={e => set("postalCode", e.target.value)} />
            </div>
          </div>

          {/* Primary Contact */}
          <div className="cm-section-title"><FaUser /> Primary Contact</div>
          <div className="cm-form-grid cols-3">
            <div className="cm-field">
              <label className="cm-label">Contact Name</label>
              <input className="cm-input" placeholder="Full name" value={form.primaryContactName}
                onChange={e => set("primaryContactName", e.target.value)} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Email <span className="req">*</span></label>
              <div className="cm-input-group">
                <FaEnvelope className="cm-input-prefix" />
                <input className="cm-input with-prefix" placeholder="email@company.com" type="email"
                  value={form.primaryContactEmail}
                  onChange={e => set("primaryContactEmail", e.target.value)} />
              </div>
            </div>
            <div className="cm-field">
              <label className="cm-label">Phone</label>
              <div className="cm-input-group">
                <FaPhone className="cm-input-prefix" />
                <input className="cm-input with-prefix" placeholder="+91 98765 43210"
                  value={form.primaryContactPhone}
                  onChange={e => set("primaryContactPhone", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Billing & Status */}
          <div className="cm-section-title"><FaTag /> Billing & Status</div>
          <div className="cm-form-grid cols-3">
            <div className="cm-field">
              <label className="cm-label">Status <span className="req">*</span></label>
              <select className="cm-fieldselect" value={form.status}
                onChange={e => set("status", e.target.value)}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="cm-field">
              <label className="cm-label">Currency <span className="req">*</span></label>
              <select className="cm-fieldselect" value={form.currency}
                onChange={e => set("currency", e.target.value)}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="cm-field">
              <label className="cm-label">Payment Terms</label>
              <select className="cm-fieldselect" value={form.paymentTerms}
                onChange={e => set("paymentTerms", e.target.value)}>
                {PAYMENT_TERMS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="cm-field">
              <label className="cm-label">Tax Number / GST</label>
              <input className="cm-input" placeholder="GST/Tax registration" value={form.taxNumber}
                onChange={e => set("taxNumber", e.target.value)} />
            </div>
            <div className="cm-field span2">
              <label className="cm-label">Tags</label>
              <TagInput tags={form.tags} onChange={v => set("tags", v)} />
              <span className="cm-field-hint">Press Enter or comma to add tags</span>
            </div>
          </div>

          {/* Notes */}
          <div className="cm-section-title">Notes</div>
          <div className="cm-field" style={{ marginBottom: 4 }}>
            <textarea className="cm-textarea" placeholder="Internal notes about this customer…"
              value={form.notes} onChange={e => set("notes", e.target.value)} />
          </div>
        </div>

        <div className="cm-modal-footer">
          <button className="btn-primary-cm" onClick={handleSave} disabled={saving}>
            <FaSave size={12} /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Add "}
          </button>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Contacts Modal ──────────────────────────────────────── */
function ContactsModal({ customer, onClose, token }) {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(blankContact());
  const [editIdx, setEditIdx] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchContacts = useCallback(async () => {
    if (!customer?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/${customer.id}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setContacts(await res.json());
    } catch { /* silent */ }
  }, [customer?.id, token]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleSetPrimary = async (contactId) => {
    try {
      await fetch(`${API_BASE_URL}/api/customers/${customer.id}/contacts/${contactId}/primary`, {
        method: "PUT", headers: { Authorization: `Bearer ${token}` }
      });
      fetchContacts(); toast.success("Primary contact updated.");
    } catch { toast.error("Failed to update primary contact."); }
  };

  const handleSaveContact = async () => {
    if (!form.name.trim()) { toast.warning("Contact name required."); return; }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.warning("Valid email required."); return; }
    setLoading(true);
    try {
      const isNew = editIdx === null;
      const url = isNew
        ? `${API_BASE_URL}/api/customers/${customer.id}/contacts`
        : `${API_BASE_URL}/api/customers/${customer.id}/contacts/${contacts[editIdx].id}`;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      toast.success(isNew ? "Contact added!" : "Contact updated!");
      setForm(blankContact()); setEditIdx(null);
      fetchContacts();
    } catch { toast.error("Failed to save contact."); } finally { setLoading(false); }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Remove this contact?")) return;
    try {
      await fetch(`${API_BASE_URL}/api/customers/${customer.id}/contacts/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contact removed."); fetchContacts();
    } catch { toast.error("Failed to remove contact."); }
  };

  return (
    <div className="cm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cm-modal lg">
        <div className="cm-modal-header">
          <div>
            <div className="cm-modal-title">Manage Contacts</div>
            <div className="cm-modal-sub">{customer?.customerName} — {contacts.length} contact(s)</div>
          </div>
          <button className="cm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="cm-modal-body">
          {contacts.length > 0 && (
            <>
              <div className="cm-section-title"><FaUsers /> Existing Contacts</div>
              <div className="cm-contacts-list" style={{ marginBottom: 24 }}>
                {contacts.map((c, i) => (
                  <div key={c.id || i} className={`cm-contact-card${c.isPrimary ? " primary-contact" : ""}`}>
                    <div className="cm-contact-avatar">{initials(c.name)}</div>
                    <div className="cm-contact-info">
                      <div className="cm-contact-name">
                        {c.name}
                        {c.isPrimary && <span className="cm-primary-badge">Primary</span>}
                      </div>
                      <div className="cm-contact-details">
                        {c.email}
                        {c.phone ? ` · ${c.phone}` : ""}
                        {c.designation ? ` · ${c.designation}` : ""}
                        {c.department ? ` · ${c.department}` : ""}
                      </div>
                    </div>
                    <div className="cm-contact-actions">
                      {!c.isPrimary && (
                        <button className="row-action row-contacts" title="Set as primary"
                          onClick={() => handleSetPrimary(c.id)}>
                          <FaStar />
                        </button>
                      )}
                      <button className="row-action row-edit" title="Edit"
                        onClick={() => { setForm({ ...c }); setEditIdx(i); }}>
                        <FaEdit />
                      </button>
                      <button className="row-action row-del" title="Remove"
                        onClick={() => handleDeleteContact(c.id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="cm-section-title">
            <FaUserPlus /> {editIdx !== null ? "Edit Contact" : "Add New Contact"}
          </div>
          <div className="cm-form-grid">
            <div className="cm-field">
              <label className="cm-label">Full Name <span className="req">*</span></label>
              <input className="cm-input" placeholder="Contact name"
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Designation / Role</label>
              <input className="cm-input" placeholder="e.g. Finance Manager"
                value={form.designation}
                onChange={e => setForm(p => ({ ...p, designation: e.target.value }))} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Department</label>
              <input className="cm-input" placeholder="e.g. Finance, HR"
                value={form.department}
                onChange={e => setForm(p => ({ ...p, department: e.target.value }))} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Email <span className="req">*</span></label>
              <input className="cm-input" type="email" placeholder="email@company.com"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Phone</label>
              <input className="cm-input" placeholder="+91 98765 43210"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="cm-field">
              <label className="cm-label">Address</label>
              <input className="cm-input" placeholder="Enter address"
                value={form.address}
                onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="cm-field" style={{ gridColumn: "1 / -1" }}>
              <label className="cm-label">Notes</label>
              <textarea className="cm-textarea" placeholder="Additional notes..."
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button className="btn-primary-cm" onClick={handleSaveContact} disabled={loading}>
              <FaSave size={12} /> {loading ? "Saving…" : editIdx !== null ? "Update Contact" : "Add Contact"}
            </button>
            {editIdx !== null && (
              <button className="btn-ghost" onClick={() => { setForm(blankContact()); setEditIdx(null); }}>
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className="cm-modal-footer">
          <button className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Confirm Modal ───────────────────────────────────────── */
function ConfirmModal({ type, customer, onClose, onConfirm }) {
  const isDeactivate = type === "deactivate";
  return (
    <div className="cm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cm-modal sm">
        <div className="cm-modal-body" style={{ padding: "36px 28px 24px", textAlign: "center" }}>
          <div className={`cm-confirm-icon ${isDeactivate ? "warning" : "danger"}`}>
            {isDeactivate ? <FaBan /> : <FaTrash />}
          </div>
          <div className="cm-confirm-title">
            {isDeactivate ? "Deactivate Customer?" : "Delete Customer?"}
          </div>
          <div className="cm-confirm-msg">
            {isDeactivate
              ? <>Deactivating <strong>{customer?.customerName}</strong> will set status to Inactive and hide it from active dropdowns. All historical data will be preserved.</>
              : <>This will permanently delete <strong>{customer?.customerName}</strong>. Customers linked to active projects cannot be deleted.</>
            }
          </div>
        </div>
        <div className="cm-modal-footer" style={{ justifyContent: "center" }}>
          <button className={isDeactivate ? "btn-warning-cm" : "btn-danger-cm"} onClick={onConfirm}>
            {isDeactivate ? <><FaBan size={12} /> Deactivate</> : <><FaTrash size={12} /> Delete</>}
          </button>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── View Customer Modal ─────────────────────────────────── */
function ViewCustomerModal({ customer, onClose, onEdit, onContacts }) {
  if (!customer) return null;
  const c = customer;

  const Detail = ({ label, value, mono }) => (
    <div className="cm-field">
      <label className="cm-label">{label}</label>
      <div style={{
        padding: "9px 12px", background: "#fafaf9", borderRadius: 8,
        border: "1px solid #e8e8e6", fontSize: 13.5, color: value ? "#222" : "#bbb",
        fontFamily: mono ? "'DM Mono', monospace" : "'DM Sans', sans-serif",
        minHeight: 38, display: "flex", alignItems: "center"
      }}>
        {value || "—"}
      </div>
    </div>
  );

  const address = [c.addressLine1, c.addressLine2, c.city, c.state, c.country, c.postalCode]
    .filter(Boolean).join(", ");

  return (
    <div className="cm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cm-modal lg">
        <div className="cm-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="cm-avatar" style={{ width: 40, height: 40, fontSize: 14 }}>
              {initials(c.customerName)}
            </div>
            <div>
              <div className="cm-modal-title">{c.customerName}</div>
              <div className="cm-modal-sub" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                {c.customerCode && <span className="cm-code-badge">{c.customerCode}</span>}
                <StatusBadge status={c.status || "Active"} />
                <TypeChip type={c.customerType} />
              </div>
            </div>
          </div>
          <button className="cm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="cm-modal-body">
          {/* Basic Info */}
          <div className="cm-section-title"><FaBuilding /> Basic Information</div>
          <div className="cm-form-grid">
            <Detail label="Customer Code" value={c.customerCode} mono />
            <Detail label="Customer Name" value={c.customerName} />
            <Detail label="Customer Type" value={c.customerType} />
            <Detail label="Industry" value={c.industry} />
            <div className="cm-field span2">
              <label className="cm-label">Website</label>
              <div style={{
                padding: "9px 12px", background: "#fafaf9", borderRadius: 8,
                border: "1px solid #e8e8e6", fontSize: 13.5, minHeight: 38,
                display: "flex", alignItems: "center"
              }}>
                {c.website ? (
                  <a href={c.website} target="_blank" rel="noopener noreferrer"
                    style={{ color: "#0052CC", textDecoration: "none" }}>{c.website}</a>
                ) : <span style={{ color: "#bbb" }}>—</span>}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="cm-section-title"><FaMapMarkerAlt /> Address</div>
          <div className="cm-form-grid">
            <div className="cm-field span2">
              <label className="cm-label">Full Address</label>
              <div style={{
                padding: "9px 12px", background: "#fafaf9", borderRadius: 8,
                border: "1px solid #e8e8e6", fontSize: 13.5, minHeight: 38,
                display: "flex", alignItems: "center", color: address ? "#222" : "#bbb"
              }}>
                {address || "—"}
              </div>
            </div>
          </div>

          {/* Primary Contact */}
          <div className="cm-section-title"><FaUser /> Primary Contact</div>
          <div className="cm-form-grid cols-3">
            <Detail label="Name" value={c.primaryContactName} />
            <div className="cm-field">
              <label className="cm-label">Email</label>
              <div style={{
                padding: "9px 12px", background: "#fafaf9", borderRadius: 8,
                border: "1px solid #e8e8e6", fontSize: 13.5, minHeight: 38,
                display: "flex", alignItems: "center"
              }}>
                {c.primaryContactEmail ? (
                  <a href={`mailto:${c.primaryContactEmail}`}
                    style={{ color: "#0052CC", textDecoration: "none" }}>{c.primaryContactEmail}</a>
                ) : <span style={{ color: "#bbb" }}>—</span>}
              </div>
            </div>
            <Detail label="Phone" value={c.primaryContactPhone} mono />
          </div>

          {/* Billing & Status */}
          <div className="cm-section-title"><FaTag /> Billing & Status</div>
          <div className="cm-form-grid cols-3">
            <Detail label="Currency" value={c.currency} mono />
            <Detail label="Payment Terms" value={c.paymentTerms} />
            <Detail label="Tax Number / GST" value={c.taxNumber} mono />
          </div>

          {/* Tags */}
          {c.tags && c.tags.length > 0 && (
            <div style={{ marginBottom: 22 }}>
              <label className="cm-label" style={{ marginBottom: 8, display: "block" }}>Tags</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {c.tags.map(t => (
                  <span key={t} className="cm-tag-chip">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {c.notes && (
            <>
              <div className="cm-section-title">Notes</div>
              <div style={{
                padding: "12px 14px", background: "#fafaf9", borderRadius: 8,
                border: "1px solid #e8e8e6", fontSize: 13.5, color: "#444",
                lineHeight: 1.6, whiteSpace: "pre-wrap"
              }}>
                {c.notes}
              </div>
            </>
          )}

          {/* Created date */}
          <div style={{ marginTop: 20, fontSize: 12, color: "#aaa", textAlign: "right" }}>
            Created: {formatDate(c.createdAt)}
          </div>
        </div>

        <div className="cm-modal-footer">
          <button className="btn-ghost" onClick={onClose}>Close</button>
          <button className="btn-ghost" onClick={() => { onClose(); onContacts(c); }}>
            <FaUsers size={12} /> Contacts
          </button>
          <button className="btn-primary-cm" onClick={() => { onClose(); onEdit(c); }}>
            <FaEdit size={12} /> Edit Customer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────────── */
export default function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [contactsCustomer, setContactsCustomer] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [viewCustomer, setViewCustomer] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
      toast.error("Unauthorized."); navigate("/");
    }
  }, [navigate, token]);

  /* ── Fetch ── */
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page - 1, size: PAGE_SIZE, sortField, sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(industryFilter && { industry: industryFilter }),
        ...(typeFilter && { customerType: typeFilter }),
      });
      const res = await fetch(`${API_BASE_URL}/api/customers/all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : data.content || []);
      setTotal(data.totalElements ?? (Array.isArray(data) ? data.length : 0));
    } catch { toast.error("Failed to load customers."); } finally { setLoading(false); }
  }, [page, search, statusFilter, industryFilter, typeFilter, sortField, sortDir, token]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  /* ── Sort ── */
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
    setPage(1);
  };

  /* ── Delete / Deactivate ── */
  const handleConfirm = async () => {
    const { type, customer } = confirmAction;
    try {
      if (type === "deactivate") {
        const res = await fetch(`${API_BASE_URL}/api/customers/update/${customer.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ ...customer, status: "Inactive" })
        });
        if (!res.ok) throw new Error();
        toast.success("Customer deactivated.");
      } else {
        const res = await fetch(`${API_BASE_URL}/api/customers/delete/${customer.id}`, {
          method: "DELETE", headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error();
        toast.success("Customer deleted.");
      }
      setConfirmAction(null); fetchCustomers();
    } catch { toast.error(type === "deactivate" ? "Failed to deactivate." : "Cannot delete — may be linked to an active project."); }
  };

  /* ── CSV Export ── */
  const handleExport = () => {
    if (!customers.length) { toast.info("No data to export."); return; }
    const cols = ["customerCode", "customerName", "customerType", "industry", "primaryContactEmail", "status", "currency", "createdAt"];
    const header = cols.join(",");
    const rows = customers.map(c => cols.map(k => `"${c[k] ?? ""}"`).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "customers.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  const exportCustomers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/export`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) throw new Error();

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "customers.csv";
      a.click();

      window.URL.revokeObjectURL(url);
      setShowExportMenu(false);
    } catch {
      toast.error("Failed to export customers.");
    }
  };

  const exportContacts = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/customers/contacts/export-csv-all`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!res.ok) throw new Error();

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "all_contacts.csv";
      a.click();

      window.URL.revokeObjectURL(url);
      setShowExportMenu(false);
    } catch {
      toast.error("Failed to export contacts.");
    }
  };

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageNums = () => {
    const pages = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pages.push(i);
    return pages;
  };

  /* ── Stats ── */
  const activeCount = customers.filter(c => c.status?.toLowerCase() === "active").length;
  const inactiveCount = customers.filter(c => c.status?.toLowerCase() === "inactive").length;

  const menuItemStyle = {
    padding: "10px 14px",
    cursor: "pointer",
    fontSize: 13,
    borderBottom: "1px solid #f0f0ee"
  };

  return (
    <>
      <style>{S}</style>
      <div className="fixed-top"><AdminNavbar /></div>

      <div className="cm-wrap">
        <div className="cm-page">
          {/* ── Header ── */}
          <div className="cm-header">
            <div>
              <h1 className="cm-title">Customer Management</h1>
              <p className="cm-subtitle">Manage your customer base, contacts, and billing preferences</p>
            </div>
            <div className="cm-actions">
              <button className="btn-ghost" onClick={fetchCustomers}>↻ Refresh</button>
              <div style={{ position: "relative" }}>
                <button
                  className="btn-ghost"
                  onClick={() => setShowExportMenu(p => !p)}
                >
                  ↓ Export CSV ▾
                </button>

                {showExportMenu && (
                  <div style={{
                    position: "absolute",
                    top: "110%",
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e2e2e2",
                    borderRadius: 8,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                    minWidth: 180,
                    zIndex: 1000
                  }}>

                    <div
                      style={menuItemStyle}
                      onClick={exportCustomers}
                    >
                      📄 Export Customers
                    </div>

                    <div
                      style={menuItemStyle}
                      onClick={exportContacts}
                    >
                      👥 Export Contacts
                    </div>

                  </div>
                )}
              </div>
              <button className="btn-primary-cm" onClick={() => { setEditCustomer(null); setShowForm(true); }}>
                + New Customer
              </button>
            </div>
          </div>

          {/* ── Stats Row ── */}
          <div className="cm-stats-row">
            <span className="cm-stats-label">Overview</span>
            <div className="cm-stat-pills">
              <div className="cm-stat-pill">
                <span className="cm-stat-dot" style={{ background: "#555" }} />
                {total} Total
              </div>
              <div className="cm-stat-pill active-pill">
                <span className="cm-stat-dot" style={{ background: "#356035" }} />
                {activeCount} Active
              </div>
              <div className="cm-stat-pill inactive-pill">
                <span className="cm-stat-dot" style={{ background: "#aaa" }} />
                {inactiveCount} Inactive
              </div>
            </div>
          </div>

          {/* ── Toolbar ── */}
          <div className="cm-toolbar">
            <div className="cm-search">
              <span className="cm-search-icon">⌕</span>
              <input placeholder="Search by name, code, or email…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="cm-select" value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="cm-select" value={industryFilter}
              onChange={e => { setIndustryFilter(e.target.value); setPage(1); }}>
              <option value="">All Industries</option>
              {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
            </select>
            <select className="cm-select" value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          {/* ── Table ── */}
          <div className="cm-table-card">
            <div className="cm-table-wrap">
              {loading ? (
                <div className="cm-loading"><span className="cm-spinner" />Loading customers…</div>
              ) : (
                <table className="cm-table">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>Sr no</th>
                      <th onClick={() => handleSort("customerCode")}>
                        <span className="th-inner">Code <SortIcon field="customerCode" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th onClick={() => handleSort("customerName")}>
                        <span className="th-inner">Customer <SortIcon field="customerName" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th>Type</th>
                      <th>Industry</th>
                      <th>Primary Email</th>
                      <th>Status</th>
                      <th>Currency</th>
                      <th onClick={() => handleSort("createdAt")}>
                        <span className="th-inner">Created <SortIcon field="createdAt" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th style={{ width: 160, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.length === 0 ? (
                      <tr>
                        <td colSpan={10}>
                          <div className="cm-empty">No customers found for the selected filters.</div>
                        </td>
                      </tr>
                    ) : customers.map((c, idx) => (
                      <tr key={c.id}>
                        <td style={{ color: "#aaa", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td><span className="cm-code-badge">{c.customerCode || "—"}</span></td>
                        <td>
                          <div className="cm-name-cell">
                            <div className="cm-avatar">{initials(c.customerName)}</div>
                            <div>
                              <div className="cm-name-text">{c.customerName}</div>
                              {c.city && <div className="cm-sub-text">{c.city}{c.country ? `, ${c.country}` : ""}</div>}
                            </div>
                          </div>
                        </td>
                        <td><TypeChip type={c.customerType} /></td>
                        <td style={{ color: "#777", fontSize: 13 }}>{c.industry || "—"}</td>
                        <td style={{ fontSize: 12.5, color: "#777" }}>
                          {c.primaryContactEmail ? (
                            <a href={`mailto:${c.primaryContactEmail}`}
                              style={{ color: "#0052CC", textDecoration: "none" }}>
                              {c.primaryContactEmail}
                            </a>
                          ) : "—"}
                        </td>
                        <td><StatusBadge status={c.status || "Active"} /></td>
                        <td style={{ fontSize: 12.5, fontWeight: 500, color: "#333", fontFamily: "'DM Mono', monospace" }}>
                          {c.currency || "—"}
                        </td>
                        <td style={{ fontSize: 12, color: "#888", fontFamily: "'DM Mono', monospace" }}>
                          {formatDate(c.createdAt)}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                            <button className="row-action row-view" title="View Details" onClick={() => setViewCustomer(c)}><FaEye /></button>
                            <button className="row-action row-contacts" title="Manage Contacts" onClick={() => setContactsCustomer(c)}><FaUsers /></button>
                            <button className="row-action row-edit" title="Edit" onClick={() => { setEditCustomer(c); setShowForm(true); }}><FaEdit /></button>
                            {c.status?.toLowerCase() !== "inactive" && (
                              <button className="row-action row-deactivate" title="Deactivate" onClick={() => setConfirmAction({ type: "deactivate", customer: c })}><FaBan /></button>
                            )}
                            <button className="row-action row-del" title="Delete" onClick={() => setConfirmAction({ type: "delete", customer: c })}><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="cm-footer">
              <span>{total} result{total !== 1 ? "s" : ""}</span>
              {totalPages > 1 && (
                <div className="cm-pagination">
                  <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                  {pageNums().map(n => (
                    <button key={n} className={`pg-btn${page === n ? " active" : ""}`}
                      onClick={() => setPage(n)}>{n}</button>
                  ))}
                  <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {viewCustomer && (
        <ViewCustomerModal
          customer={viewCustomer}
          onClose={() => setViewCustomer(null)}
          onEdit={(c) => { setEditCustomer(c); setShowForm(true); }}
          onContacts={(c) => setContactsCustomer(c)}
        />
      )}
      {showForm && (
        <CustomerModal
          customer={editCustomer}
          token={token}
          onClose={() => { setShowForm(false); setEditCustomer(null); }}
          onSaved={() => { setShowForm(false); setEditCustomer(null); fetchCustomers(); }}
        />
      )}
      {contactsCustomer && (
        <ContactsModal
          customer={contactsCustomer}
          token={token}
          onClose={() => setContactsCustomer(null)}
        />
      )}
      {confirmAction && (
        <ConfirmModal
          type={confirmAction.type}
          customer={confirmAction.customer}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}